import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ── FY Snapshot Modal ─────────────────────────────────────────────────────────
function FYSnapshotModal({ backupId, fileName, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['backup-snapshot', backupId],
    queryFn: () => api.get(`/backup/snapshot/${backupId}`).then(r => r.data),
  });

  const fmt = (n) => n?.toLocaleString('en-IN') ?? '—';
  const fmtCur = (n) => n != null ? `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

  return (
    <div style={{ position:'fixed', inset:0, background:'#00000088', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, padding:28, width:520, maxWidth:'95vw', maxHeight:'85vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:16 }}>FY Snapshot</div>
            <div style={{ color:'var(--text-muted)', fontSize:12, marginTop:2 }}>{fileName}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'var(--text-muted)' }}>✕</button>
        </div>

        {isLoading && <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading snapshot…</div>}
        {error && <div style={{ color:'#ef4444', padding:20 }}>No snapshot found in this archive (older backup).</div>}

        {data && (
          <>
            <div style={{ background:'#6366f111', border:'1px solid #6366f133', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13 }}>
              <span style={{ fontWeight:600, color:'#6366f1' }}>FY {data.fy}</span>
              <span style={{ color:'var(--text-muted)', marginLeft:10 }}>{data.fy_period?.from} → {data.fy_period?.to}</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                { label:'Active Employees', value: fmt(data.employees?.active), icon:'👥' },
                { label:'Total Clients', value: fmt(data.clients?.total), icon:'🏢' },
                { label:'Projects This FY', value: fmt(data.projects?.created_this_fy), icon:'📁' },
                { label:'Work Entries', value: fmt(data.work_entries?.count), icon:'📋' },
                { label:'Total Hours Logged', value: `${fmt(data.work_entries?.total_hours)} hrs`, icon:'⏱' },
                { label:'Invoices Raised', value: fmt(data.invoices?.count), icon:'🧾' },
                { label:'Total Revenue', value: fmtCur(data.invoices?.revenue), icon:'💰' },
                { label:'Total Payroll', value: fmtCur(data.payroll?.total_paid), icon:'💳' },
                { label:'Attendance Records', value: fmt(data.attendance?.records), icon:'📅' },
                { label:'Database Size', value: `${data.database_size_mb} MB`, icon:'🗄' },
              ].map(s => (
                <div key={s.label} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:3 }}>{s.icon} {s.label}</div>
                  <div style={{ fontWeight:700, fontSize:15 }}>{s.value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:14, fontSize:11, color:'var(--text-muted)', textAlign:'right' }}>
              Generated: {new Date(data.generated_at).toLocaleString('en-IN')}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Restore Confirm Modal ─────────────────────────────────────────────────────
function RestoreModal({ backup, onClose, onConfirm, isLoading }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'#00000088', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--bg-card)', border:'1px solid #ef444466', borderRadius:14, padding:28, width:460, maxWidth:'95vw' }}>
        <div style={{ fontSize:18, fontWeight:700, marginBottom:10, color:'#ef4444' }}>⚠ Restore Database</div>
        <p style={{ color:'var(--text-muted)', fontSize:13, lineHeight:1.6, marginBottom:16 }}>
          This will <strong>overwrite the current database</strong> with the backup from:<br/>
          <strong style={{ color:'var(--text)' }}>{backup.file_name}</strong>
        </p>
        <div style={{ background:'#ef444411', border:'1px solid #ef444433', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#ef4444', marginBottom:20 }}>
          All data added after this backup was taken will be <strong>permanently lost</strong>. This cannot be undone. Make sure you have a current backup before restoring.
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:7, padding:'9px 18px', fontWeight:600, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} disabled={isLoading} style={{ background:'#ef4444', color:'#fff', border:'none', borderRadius:7, padding:'9px 18px', fontWeight:600, fontSize:13, cursor:'pointer', opacity:isLoading?0.6:1 }}>
            {isLoading ? 'Restoring…' : 'Yes, Restore Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}
function fmtMB(mb) {
  if (!mb && mb !== 0) return '—';
  return parseFloat(mb) >= 1024 ? `${(mb/1024).toFixed(1)} GB` : `${parseFloat(mb).toFixed(1)} MB`;
}
function timeSince(d) {
  if (!d) return '—';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

const TYPE_LABELS = { manual:'Manual', daily:'Daily Auto', weekly:'Weekly Auto', monthly:'Monthly Auto', fy_archive:'FY Archive' };
const TYPE_COLORS = { manual:'#6366f1', daily:'#10b981', weekly:'#3b82f6', monthly:'#8b5cf6', fy_archive:'#f59e0b' };
const STATUS_COLOR = { running:'#f59e0b', success:'#10b981', failed:'#ef4444' };

function StatusPill({ status }) {
  return (
    <span style={{ background: STATUS_COLOR[status]+'22', color: STATUS_COLOR[status], padding:'2px 8px', borderRadius:12, fontSize:11, fontWeight:600, textTransform:'uppercase' }}>
      {status === 'running' ? '⟳ Running' : status === 'success' ? '✓ Success' : '✗ Failed'}
    </span>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:'18px 20px', minWidth:140 }}>
      <div style={{ color:'var(--text-muted)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:700, color: color||'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:3 }}>{sub}</div>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function BackupPage() {
  const qc = useQueryClient();
  const [pollingId, setPollingId] = useState(null);
  const [snapshotBackup, setSnapshotBackup] = useState(null);
  const [restoreBackup, setRestoreBackup] = useState(null);
  const pollRef = useRef(null);

  const { data: stats }   = useQuery({ queryKey:['backup-stats'],  queryFn: () => api.get('/backup/stats').then(r=>r.data),  refetchInterval: 30000 });
  const { data: backups=[], isLoading } = useQuery({ queryKey:['backup-list'], queryFn: () => api.get('/backup/list').then(r=>r.data), refetchInterval: pollingId ? 3000 : 30000 });
  const { data: cloudStatus } = useQuery({ queryKey:['cloud-status'], queryFn: () => api.get('/backup/cloud-status').then(r=>r.data), refetchInterval: 60000 });

  // Poll while a backup is running
  useEffect(() => {
    if (!pollingId) return;
    const running = backups.find(b => b.id === pollingId);
    if (running && running.status !== 'running') {
      setPollingId(null);
      qc.invalidateQueries({ queryKey:['backup-stats'] });
      if (running.status === 'success') toast.success(`Backup complete — ${fmtMB(running.file_size_mb)}`);
      else toast.error(`Backup failed: ${running.error_msg}`);
    }
  }, [backups, pollingId, qc]);

  const createMutation = useMutation({
    mutationFn: (type) => api.post('/backup/create', { type }).then(r=>r.data),
    onSuccess: (data) => {
      setPollingId(data.log_id);
      qc.invalidateQueries({ queryKey:['backup-list'] });
      toast.success('Backup started — this may take a minute');
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed to start backup'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/backup/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey:['backup-list'] }); qc.invalidateQueries({ queryKey:['backup-stats'] }); toast.success('Backup deleted'); },
    onError: () => toast.error('Delete failed'),
  });

  const cloudSyncMutation = useMutation({
    mutationFn: (id) => api.post(`/backup/cloud-sync/${id}`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey:['backup-list'] }); qc.invalidateQueries({ queryKey:['cloud-status'] }); toast.success('Synced to cloud'); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Cloud sync failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => api.post(`/backup/restore/${id}`).then(r => r.data),
    onSuccess: () => { setRestoreBackup(null); toast.success('Database restored successfully — please refresh the app'); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Restore failed'),
  });

  async function download(backup) {
    const toastId = toast.loading('Preparing download…');
    try {
      const res = await api.get(`/backup/download/${backup.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.file_name || 'backup.zip';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success('Download started', { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Download failed', { id: toastId });
    }
  }

  function confirmDelete(backup) {
    if (window.confirm(`Delete backup "${backup.file_name}"? This cannot be undone.`))
      deleteMutation.mutate(backup.id);
  }

  const hasRunning = backups.some(b => b.status === 'running');

  return (
    <div style={{ padding:'28px 24px', maxWidth:1100, margin:'0 auto' }}>
      {snapshotBackup && <FYSnapshotModal backupId={snapshotBackup.id} fileName={snapshotBackup.file_name} onClose={() => setSnapshotBackup(null)} />}
      {restoreBackup && <RestoreModal backup={restoreBackup} onClose={() => setRestoreBackup(null)} onConfirm={() => restoreMutation.mutate(restoreBackup.id)} isLoading={restoreMutation.isPending} />}
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:0 }}>Backup & Recovery</h1>
          <p style={{ color:'var(--text-muted)', margin:'4px 0 0', fontSize:13 }}>
            Database snapshots with uploads folder • Auto-scheduled daily, weekly, monthly
          </p>
        </div>
        <button
          onClick={() => createMutation.mutate('manual')}
          disabled={createMutation.isPending || hasRunning}
          style={{ background:'#6366f1', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontWeight:600, fontSize:13, cursor:'pointer', opacity: (createMutation.isPending||hasRunning) ? 0.6 : 1 }}
        >
          {createMutation.isPending || hasRunning ? '⟳ Backup Running…' : '＋ Create Backup Now'}
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:28 }}>
        <StatCard label="Last Backup" value={stats?.last_backup ? timeSince(stats.last_backup.started_at) : 'Never'} sub={stats?.last_backup ? fmtDate(stats.last_backup.started_at) : ''} />
        <StatCard label="DB Size" value={fmtMB(stats?.db_size_mb)} sub="Current database" color="#3b82f6" />
        <StatCard label="Storage Used" value={fmtMB(stats?.storage_used_mb)} sub="All backups on disk" color="#8b5cf6" />
        <StatCard label="Total Backups" value={(stats?.counts ? Object.values(stats.counts).reduce((a,b)=>a+b,0) : 0)} sub="Across all types" />
        <StatCard label="Next Daily" value={stats?.next_daily ? new Date(stats.next_daily).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '—'} sub="Auto at 2:00 AM IST" color="#10b981" />
      </div>

      {/* Schedule info */}
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 20px', marginBottom:28 }}>
        <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Auto-Schedule</div>
        <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
          {[
            { label:'Daily', desc:'2:00 AM IST', keep:'30 kept', color:'#10b981' },
            { label:'Weekly', desc:'Sun 2:30 AM IST', keep:'12 kept', color:'#3b82f6' },
            { label:'Monthly', desc:'1st 3:00 AM IST', keep:'24 kept', color:'#8b5cf6' },
            { label:'FY Archive', desc:'31 March 11:59 PM', keep:'Permanent', color:'#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:s.color, display:'inline-block' }}/>
              <span style={{ fontWeight:600, fontSize:12 }}>{s.label}</span>
              <span style={{ color:'var(--text-muted)', fontSize:12 }}>{s.desc} · {s.keep}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cloud status */}
      <div style={{ background:'var(--bg-card)', border:`1px solid ${cloudStatus?.ok ? '#10b98133' : cloudStatus?.configured ? '#ef444433' : 'var(--border)'}`, borderRadius:12, padding:'14px 20px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:20 }}>☁</span>
          <div>
            <div style={{ fontWeight:600, fontSize:13 }}>
              Cloud Backup
              {cloudStatus?.configured ? (
                cloudStatus?.ok
                  ? <span style={{ color:'#10b981', marginLeft:8, fontSize:11 }}>● Connected — {cloudStatus.provider} / {cloudStatus.bucket}</span>
                  : <span style={{ color:'#ef4444', marginLeft:8, fontSize:11 }}>● Error: {cloudStatus.error}</span>
              ) : (
                <span style={{ color:'var(--text-muted)', marginLeft:8, fontSize:11 }}>Not configured</span>
              )}
            </div>
            {cloudStatus?.configured && (
              <div style={{ color:'var(--text-muted)', fontSize:12, marginTop:2 }}>
                {cloudStatus.synced_count} synced · {cloudStatus.unsynced_count} local only
              </div>
            )}
            {!cloudStatus?.configured && (
              <div style={{ color:'var(--text-muted)', fontSize:12, marginTop:2 }}>Add CLOUD_KEY_ID, CLOUD_SECRET, CLOUD_BUCKET to server .env to enable</div>
            )}
          </div>
        </div>
      </div>

      {/* Backup list */}
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:13, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>Backup History</span>
          <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:12 }}>Last 100 entries</span>
        </div>

        {isLoading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Loading…</div>
        ) : backups.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:'var(--text-muted)' }}>
            <div style={{ fontSize:36, marginBottom:10 }}>🗄</div>
            <div style={{ fontWeight:600, marginBottom:6 }}>No backups yet</div>
            <div style={{ fontSize:13 }}>Click "Create Backup Now" to take your first snapshot</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  {['Type','Status','Started','Duration','File Size','DB Size','Cloud','Created By','Actions'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', color:'var(--text-muted)', fontWeight:600, fontSize:11, textTransform:'uppercase', letterSpacing:0.5, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map((b, i) => {
                  const dur = b.started_at && b.finished_at
                    ? Math.round((new Date(b.finished_at) - new Date(b.started_at)) / 1000)
                    : null;
                  return (
                    <tr key={b.id} style={{ borderBottom: i < backups.length-1 ? '1px solid var(--border)' : 'none', background: b.status==='running' ? 'var(--bg)' : 'transparent' }}>
                      <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                        <span style={{ background: TYPE_COLORS[b.backup_type]+'22', color: TYPE_COLORS[b.backup_type], padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                          {TYPE_LABELS[b.backup_type] || b.backup_type}
                        </span>
                      </td>
                      <td style={{ padding:'12px 16px' }}><StatusPill status={b.status}/></td>
                      <td style={{ padding:'12px 16px', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                        {fmtDate(b.started_at)}
                        <div style={{ fontSize:11, marginTop:2 }}>{timeSince(b.started_at)}</div>
                      </td>
                      <td style={{ padding:'12px 16px', color:'var(--text-muted)' }}>
                        {b.status==='running' ? <span style={{ color:'#f59e0b' }}>⟳ In progress</span> : dur ? `${dur}s` : '—'}
                      </td>
                      <td style={{ padding:'12px 16px', fontWeight: b.file_size_mb ? 600 : 400, color: b.file_size_mb ? 'var(--text)' : 'var(--text-muted)' }}>
                        {fmtMB(b.file_size_mb)}
                      </td>
                      <td style={{ padding:'12px 16px', color:'var(--text-muted)' }}>{fmtMB(b.db_size_mb)}</td>
                      <td style={{ padding:'12px 16px' }}>
                        {b.cloud_path
                          ? <span title={`Synced: ${fmtDate(b.cloud_synced_at)}`} style={{ color:'#10b981', fontSize:12, fontWeight:600, cursor:'help' }}>☁ Synced</span>
                          : b.status === 'success' && cloudStatus?.configured
                            ? <span style={{ color:'var(--text-muted)', fontSize:12 }}>— Local</span>
                            : <span style={{ color:'var(--text-muted)', fontSize:12 }}>—</span>
                        }
                      </td>
                      <td style={{ padding:'12px 16px', color:'var(--text-muted)' }}>{b.created_by_name || (b.created_by ? 'Auto' : 'System')}</td>
                      <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                        {b.status === 'running' ? null : (
                          <div style={{ display:'flex', gap:8 }}>
                            {b.status === 'success' && (
                              <>
                                <button
                                  onClick={() => download(b)}
                                  title="Download backup"
                                  style={{ background:'#10b98122', color:'#10b981', border:'1px solid #10b98144', borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}
                                >
                                  ↓ Download
                                </button>
                                {b.backup_type === 'fy_archive' && (
                                  <button
                                    onClick={() => setSnapshotBackup(b)}
                                    title="View FY Snapshot"
                                    style={{ background:'#f59e0b22', color:'#f59e0b', border:'1px solid #f59e0b44', borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}
                                  >
                                    📊 Snapshot
                                  </button>
                                )}
                                {cloudStatus?.configured && !b.cloud_path && (
                                  <button
                                    onClick={() => cloudSyncMutation.mutate(b.id)}
                                    disabled={cloudSyncMutation.isPending}
                                    title="Upload to cloud"
                                    style={{ background:'#3b82f622', color:'#3b82f6', border:'1px solid #3b82f644', borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}
                                  >
                                    ☁ Sync
                                  </button>
                                )}
                                <button
                                  onClick={() => setRestoreBackup(b)}
                                  title="Restore this backup"
                                  style={{ background:'#6366f122', color:'#6366f1', border:'1px solid #6366f144', borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}
                                >
                                  ↺ Restore
                                </button>
                              </>
                            )}
                            {b.status === 'failed' && b.error_msg && (
                              <span title={b.error_msg} style={{ color:'#ef4444', fontSize:11, cursor:'help' }}>⚠ Error</span>
                            )}
                            <button
                              onClick={() => confirmDelete(b)}
                              disabled={deleteMutation.isPending}
                              title="Delete backup"
                              style={{ background:'#ef444422', color:'#ef4444', border:'1px solid #ef444444', borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}
                            >
                              🗑
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FY Archive manual trigger */}
      <div style={{ marginTop:16, padding:'14px 18px', background:'#f59e0b11', border:'1px solid #f59e0b33', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
        <div>
          <span style={{ fontWeight:600, fontSize:13, color:'#f59e0b' }}>FY Archive</span>
          <span style={{ color:'var(--text-muted)', fontSize:12, marginLeft:10 }}>Take a permanent FY-end archive at any time</span>
        </div>
        <button
          onClick={() => createMutation.mutate('fy_archive')}
          disabled={createMutation.isPending || hasRunning}
          style={{ background:'#f59e0b', color:'#fff', border:'none', borderRadius:7, padding:'8px 16px', fontWeight:600, fontSize:12, cursor:'pointer', opacity:(createMutation.isPending||hasRunning)?0.6:1 }}
        >
          Create FY Archive
        </button>
      </div>
    </div>
  );
}
